import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  Box,
  Paper,
  Chip,
  Divider
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { api as axios } from '../../api';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Transactions from '../Transactions/transaction.table';
import ProChart from '../../components/ProChart/ProChart';
import { logoWrapper, priceWrapper } from '../../utils';
import SwapDiv from '../../components/SwapDiv';
import TreasuryBackingPublic from '../../components/TreasuryBackingPublic';

import {
  PRIMARY_CHAIN_CODES,
  REDEMPTION_TYPE_CODES,
  PROVENANCE_TYPE_CODES,
  COLLATERAL_TYPE_CODES,
  RISK_PROFILE_CODES,
  RESERVE_TYPE_CODES,
  BRIDGED_CHAINS_CODES,
  COLLATERALIZATION_TYPE_CODES
} from '../../screens/editForms/stableCodeDictionaryNew';

// Helper function to resolve code to label
const resolveLabel = (code, codeArray) => {
  if (!code || !codeArray) return 'N/A';
  const options = codeArray[0]?.options || codeArray;
  const item = options.find((c) => c.code === code);
  return item ? item.label : code;
};

// Helper function to resolve multiple codes (for multi-select fields)
const resolveMultipleLabels = (codes, codeArray) => {
  if (!codes || !Array.isArray(codes) || codes.length === 0) return 'N/A';
  const options = codeArray[0]?.options || codeArray;
  return codes
    .map((code) => {
      const item = options.find((c) => c.code === code);
      return item ? item.label : code;
    })
    .join(', ');
};

const DetailRow = ({ label, value, color = '#f0f0f0' }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.75,
      gap: 2
    }}
  >
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: '#fff', minWidth: '40%' }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ color, textAlign: 'right', flex: 1, wordBreak: 'break-word' }}
    >
      {value !== null && value !== undefined && value !== '' ? value : 'N/A'}
    </Typography>
  </Box>
);

const DetailBlock = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: '#fff', mb: 0.5 }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: '#f0f0f0', wordBreak: 'break-word', lineHeight: 1.6 }}
    >
      {value !== null && value !== undefined && value !== '' ? value : 'N/A'}
    </Typography>
  </Box>
);

export default function StableCoinPage() {
  const { identitySymbol } = useParams();
  const [token, setToken] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!identitySymbol) return;
    setLoading(true);
    axios
      .get(`/crypto/get_stable_coin?symbol=${identitySymbol}`)
      .then((res) => {
        if (res.data?.status) {
          setToken(res.data.stock[0]);
        }
      })
      .finally(() => setLoading(false));
  }, [identitySymbol]);

  const priceChange = token.currentPrice - token.price1hour;
  const priceChangePercent =
    token.currentPrice && token.price1hour
      ? Math.abs((priceChange / token.currentPrice) * 100)
      : 0;
  const isPositive = priceChange > 0;

  return (
    <>
      <Helmet>
        <title>
          {token.name || identitySymbol} -{' '}
          {token?.currentPrice?.toFixed(5) ?? ''} | Stablecoin Details
        </title>
      </Helmet>

      {loading && (
        <Box>
          <LinearProgress sx={{ backgroundColor: 'grey' }} />
        </Box>
      )}

      {!loading && (
        <Box sx={{ p: { xs: 2, md: 4 }, ml: { xs: 4 } }}>
          <Grid container spacing={3} sx={{ ml: '-55px' }}>
            <SwapDiv token={identitySymbol} />

            {/* Left Column - Token Info */}
            <Grid item xs={12} md={5} mt={4} sx={{ order: { xs: 1, md: 0 } }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: 'rgba(4, 10, 8, 0.72)',
                  borderRadius: 3,
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}
              >
                {/* Token Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={logoWrapper(token.logo)}
                    alt={token.identitySymbol}
                    sx={{ width: 56, height: 56 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}
                    >
                      {token?.identitySymbol}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                      {token?.name}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Price Section */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#9ca3af',
                      mb: 1.5,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    {token?.identitySymbol} Price
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 2,
                      flexWrap: 'wrap'
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ color: 'white', fontWeight: 600 }}
                    >
                      ${priceWrapper(token?.currentPrice)}
                    </Typography>
                    {token.currentPrice && token.price1hour && (
                      <Chip
                        icon={
                          isPositive ? (
                            <ArrowUpwardIcon sx={{ fontSize: '16px' }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: '16px' }} />
                          )
                        }
                        label={`${priceChangePercent.toFixed(2)}%`}
                        size="medium"
                        sx={{
                          backgroundColor: isPositive
                            ? 'rgba(16, 185, 129, 0.15)'
                            : 'rgba(239, 83, 80, 0.15)',
                          color: isPositive ? '#10B981' : '#EF5350',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          '& .MuiChip-icon': { color: 'inherit' }
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Reserve & Collateral */}
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Reserve & Collateral
                  </Typography>
                  <Stack
                    spacing={0}
                    divider={
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                    }
                  >
                    <DetailRow label="Issuer" value={token?.issuer} />
                    <DetailRow
                      label="Reserve Type"
                      value={resolveMultipleLabels(
                        Array.isArray(token.reserve_type)
                          ? token.reserve_type
                          : [token.reserve_type],
                        RESERVE_TYPE_CODES
                      )}
                      color="#10B981"
                    />
                    <DetailRow
                      label="Collateral Architecture"
                      value={resolveLabel(
                        token.collateral_architecture,
                        COLLATERAL_TYPE_CODES
                      )}
                    />
                    <DetailRow
                      label="Collateralization Type"
                      value={resolveLabel(
                        token.collateralization_type,
                        COLLATERALIZATION_TYPE_CODES
                      )}
                    />
                    <DetailRow
                      label="Provenance"
                      value={resolveLabel(
                        token.provenance,
                        PROVENANCE_TYPE_CODES
                      )}
                    />
                    <DetailRow
                      label="Status"
                      value={token?.status ? 'Active' : 'Inactive'}
                      color={token?.status ? '#10B981' : '#EF5350'}
                    />
                  </Stack>
                </Box>

                {/* Valuation & Transparency */}
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Valuation & Transparency
                  </Typography>
                  <Stack
                    spacing={0}
                    divider={
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                    }
                  >
                    <DetailRow
                      label="Valuation Method"
                      value={token.valuation_method}
                    />
                    <DetailRow
                      label="Pegging Mechanism"
                      value={token.pegging_mechanism}
                    />
                    <DetailRow
                      label="Transparency Level"
                      value={token.transparency}
                    />
                    <DetailRow
                      label="Treasury Management"
                      value={token.treasury_management}
                    />
                  </Stack>
                </Box>

                <TreasuryBackingPublic tokenType="STABLECOIN" tokenId={token?.id} />

                {/* Risk & Redemption */}
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Risk & Redemption
                  </Typography>
                  <Stack
                    spacing={0}
                    divider={
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                    }
                  >
                    <DetailRow
                      label="Risk Profile"
                      value={resolveLabel(
                        token.risk_profile,
                        RISK_PROFILE_CODES
                      )}
                    />
                    <DetailRow
                      label="Redemption Type"
                      value={resolveLabel(
                        token.redemption_type,
                        REDEMPTION_TYPE_CODES
                      )}
                    />
                  </Stack>
                </Box>

                {/* Blockchain & Network */}
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Blockchain & Network
                  </Typography>
                  <Stack
                    spacing={0}
                    divider={
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                    }
                  >
                    <DetailRow
                      label="Primary Chain"
                      value={resolveLabel(
                        token.primary_chain,
                        PRIMARY_CHAIN_CODES
                      )}
                    />
                    <DetailRow
                      label="Bridged Chains"
                      value={resolveMultipleLabels(
                        Array.isArray(token.bridged_chains)
                          ? token.bridged_chains
                          : [token.bridged_chains],
                        BRIDGED_CHAINS_CODES
                      )}
                    />
                  </Stack>
                </Box>

                {/* Action Buttons */}
                <Stack spacing={2}>
                  <Button
                    variant="outlined"
                    color="success"
                    href="https://pecuscan.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      borderWidth: 2,
                      fontWeight: 600,
                      '&:hover': { borderWidth: 2 }
                    }}
                  >
                    View on Pecuscan
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      backgroundColor: '#10B981',
                      color: 'white',
                      fontWeight: 600,
                      '&:hover': { backgroundColor: '#059669' }
                    }}
                  >
                    Trade {token.identitySymbol}
                  </Button>
                </Stack>
              </Paper>
            </Grid>

            {/* Right Column - Chart and Extended Details */}
            <Grid
              item
              xs={12}
              md={7}
              sx={{ marginTop: { xs: 4, md: 4 }, order: { xs: 0, md: 1 } }}
            >
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: 'rgba(4, 10, 8, 0.72)',
                  borderRadius: 3,
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}
              >
                {/* Chart Component */}
                <Box sx={{ minHeight: 400 }}>
                  <ProChart symbol={identitySymbol} chartSymbol={token.symbol} />
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Description */}
                {token?.description && (
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: '#10B981', mb: 2, fontWeight: 700 }}
                    >
                      About {token.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#f0f0f0', lineHeight: 1.8 }}
                    >
                      {token.description}
                    </Typography>
                  </Box>
                )}

                {/* Key Features */}
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Key Features
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Collateral Type"
                        value={resolveLabel(
                          token.collateral_architecture,
                          COLLATERAL_TYPE_CODES
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Risk Level"
                        value={resolveLabel(
                          token.risk_profile,
                          RISK_PROFILE_CODES
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Redemption"
                        value={resolveLabel(
                          token.redemption_type,
                          REDEMPTION_TYPE_CODES
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Primary Chain"
                        value={resolveLabel(
                          token.primary_chain,
                          PRIMARY_CHAIN_CODES
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            {/* Transactions Table - Full Width */}
            <Grid item xs={12} mt={2} sx={{ order: { xs: 2, md: 2 } }}>
              <Transactions />
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
}
